import { X } from 'lucide-react';
import {
  COMPARE_LABELS,
  DIMENSION_LABELS,
  SECTION_DIMENSIONS,
  type AnalyticsFilterOptions,
  type AnalyticsSection,
  analyticsCompareSchema,
} from '#shared/types';
import { Button } from '#client/components/ui/Button';
import { Segmented } from '#client/components/ui/Segmented';
import { Select } from '#client/components/ui/Select';
import { PRESET_LABELS, type RangePreset } from './filters.logic';
import type { AnalyticsFilterState } from './useAnalyticsFilters';

interface AnalyticsFilterBarProps {
  section: AnalyticsSection;
  filters: AnalyticsFilterState;
  options?: AnalyticsFilterOptions | undefined;
}

const PRESETS: RangePreset[] = ['7d', '30d', '90d', 'qtd', 'ytd'];

/**
 * The controls every section shares.
 *
 * Presets first, because they are what people actually use — a date picker is
 * for the one time in ten somebody wants a specific fortnight, and putting it
 * first makes the nine common cases take three clicks each.
 *
 * Everything writes to the URL, so any state of this bar is a link. That is the
 * same reason the worklist's filters live in the query string.
 */
export function AnalyticsFilterBar({ section, filters, options }: AnalyticsFilterBarProps) {
  const dimensions: readonly string[] = SECTION_DIMENSIONS[section];

  return (
    <div className="c-an-filters">
      <Segmented
        label="Period"
        size="sm"
        value={filters.preset}
        onChange={filters.setPreset}
        options={[
          ...PRESETS.map((preset) => ({ value: preset, label: PRESET_LABELS[preset] })),
          { value: 'custom', label: PRESET_LABELS.custom },
        ]}
      />

      {/* Shown only for a custom range: two date inputs permanently on screen
          is a lot of chrome for a control most visits never touch. */}
      {filters.preset === 'custom' && <CustomRangeInputs filters={filters} />}

      {dimensions.length > 1 && (
        <Select
          label="Break down"
          hideLabel
          value={filters.by}
          onChange={(choice) => filters.setBy(choice)}
          options={dimensions.map((dimension) => ({
            value: dimension,
            label: DIMENSION_LABELS[dimension] ?? dimension,
          }))}
        />
      )}

      <Select
        label="Compare with"
        hideLabel
        value={filters.compare}
        onChange={(choice) => filters.setCompare(choice)}
        options={analyticsCompareSchema.options.map((value) => ({
          value,
          label: COMPARE_LABELS[value],
        }))}
      />

      {options && <BenchSelect filters={filters} departments={options.departments} />}

      {filters.isFiltered && (
        <Button variant="ghost" size="sm" icon={<X size={14} />} onClick={filters.clearAll}>
          Clear filters
        </Button>
      )}
    </div>
  );
}

function CustomRangeInputs({ filters }: { filters: AnalyticsFilterState }) {
  return (
    <div className="c-an-filters__dates">
      <label className="c-an-filters__date">
        <span className="sr-only">From</span>
        <input
          type="date"
          value={filters.from}
          max={filters.to}
          onChange={(event) => filters.setRange(event.target.value, filters.to)}
        />
      </label>
      <span aria-hidden="true">–</span>
      <label className="c-an-filters__date">
        <span className="sr-only">To</span>
        <input
          type="date"
          value={filters.to}
          min={filters.from}
          onChange={(event) => filters.setRange(filters.from, event.target.value)}
        />
      </label>
    </div>
  );
}

function BenchSelect({
  filters,
  departments,
}: {
  filters: AnalyticsFilterState;
  departments: AnalyticsFilterOptions['departments'];
}) {
  return (
    <Select
      label="Bench"
      hideLabel
      value={String(filters.departmentIds[0] ?? '')}
      onChange={(choice) => filters.setIds('department_ids', choice ? [Number(choice)] : [])}
      placeholder="Every bench"
      options={departments.map((department) => ({
        value: String(department.id),
        label: department.name,
      }))}
    />
  );
}
